import React from "react";
import { Building, MapPin, Search, Loader2 } from "lucide-react";
import type { SearchData, SearchMode } from "../types";
import type {
  CourtOptionPaginationControls,
  SearchOption,
} from "../types/searchSessions";
import { getMaxDate } from "../utils/Date";
import DateInput from "react-calendar-ui-kit";
import {
  DistrictCourtSessionFields,
  HighCourtSessionFields,
} from "./session-search";

interface SearchFormProps {
  searchMode: SearchMode;
  searchData: SearchData;
  isSearching: boolean;
  onSearchModeChange: (mode: SearchMode) => void;
  onInputChange: (field: keyof SearchData, value: string) => void;
  onSearch: () => void;
  highCourts: SearchOption[];
  states: SearchOption[];
  benches: SearchOption[];
  benchRooms: SearchOption[];
  districts: SearchOption[];
  complexes: SearchOption[];
  establishments: SearchOption[];
  courtRooms: SearchOption[];
  courtEstablishment?: boolean;
  courtOptionControls?: CourtOptionPaginationControls;
}

const SearchForm: React.FC<SearchFormProps> = ({
  searchMode,
  searchData,
  isSearching,
  onSearchModeChange,
  onInputChange,
  onSearch,
  highCourts,
  states,
  benches,
  benchRooms,
  districts,
  complexes,
  establishments,
  courtRooms,
  courtEstablishment,
  courtOptionControls,
}) => {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSearch();
      }}
      className="bg-white rounded-xl shadow-sm overflow-visible md:p-6 p-4"
    >
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">
        Search Courtroom Sessions
      </h2>
      {/* Search Mode Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <button
          type="button"
          onClick={() => onSearchModeChange("cnr")}
          className={`p-4 rounded-lg border-2 transition-all ${
            searchMode === "cnr"
              ? "border-blue-500 bg-blue-50 text-blue-700"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Search size={20} />
            <span className="font-medium">CNR Number</span>
          </div>
          <p className="text-sm text-start text-gray-600">
            Search by 16-digit CNR
          </p>
        </button>
        <button
          type="button"
          onClick={() => onSearchModeChange("highCourt")}
          className={`p-4 rounded-lg border-2 transition-all ${
            searchMode === "highCourt"
              ? "border-blue-500 bg-blue-50 text-blue-700"
              : "border-gray-200 hover:border-gray-300"
          } `}
        >
          <div className="flex items-center gap-2 mb-2">
            <Building size={20} />
            <span className="font-medium">High Court</span>
          </div>
          <p className="text-sm text-start text-gray-600">
            Search by High Court Bench
          </p>
        </button>

        <button
          type="button"
          onClick={() => onSearchModeChange("district")}
          className={`p-4 rounded-lg border-2 transition-all ${
            searchMode === "district"
              ? "border-blue-500 bg-blue-50 text-blue-700"
              : "border-gray-200 hover:border-gray-300"
          } `}
        >
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={20} />
            <span className="font-medium">District Court</span>
          </div>
          <p className="text-sm text-start text-gray-600">
            Search by District Court
          </p>
        </button>
      </div>

      <div className="space-y-1 grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* CNR Search */}
        {searchMode === "cnr" && (
          <div>
            <label
              htmlFor="cnr"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              CNR / Case Number *
            </label>
            <input
              type="text"
              name="cnr-search" // 👈 Browser saves suggestions for this key
              autoComplete="on"
              placeholder="e.g.,MHAU019999992015 "
              value={searchData.cnr}
              onChange={(e) => onInputChange("cnr", e.target.value)}
              disabled={isSearching}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={16}
            />
          </div>
        )}

        {searchMode === "highCourt" && (
          <HighCourtSessionFields
            searchData={searchData}
            highCourts={highCourts}
            benches={benches}
            benchRooms={benchRooms}
            onInputChange={onInputChange}
            disabled={isSearching}
            standalone={false}
            controls={courtOptionControls}
          />
        )}

        {searchMode === "district" && (
          <DistrictCourtSessionFields
            searchData={searchData}
            states={states}
            districts={districts}
            complexes={complexes}
            establishments={establishments}
            courtRooms={courtRooms}
            courtEstablishment={courtEstablishment}
            onInputChange={onInputChange}
            disabled={isSearching}
            standalone={false}
            controls={courtOptionControls}
          />
        )}

        <div>
          <label
            htmlFor="date"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Date *
          </label>
          <DateInput
            value={searchData.date}
            onChange={(value) => onInputChange("date", value)}
            disabled={isSearching}
            max={getMaxDate()}
            theme={{
              selectWidth: "140px",
              yearButtonWidth: "90px",
            }}
          />
        </div>

        <div>
          <label
            htmlFor="streaming"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Streaming Status *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2  items-center gap-3 ">
            <div>
              <select
                value={searchData.status ?? ""}
                onChange={(e) => onInputChange("status", e.target.value)}
                disabled={isSearching}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="live">Live</option>
                <option value="available">Available</option>
              </select>
            </div>

            <div>
              <button
                type="submit"
                // onClick={onSearch}
                disabled={isSearching}
                className={`flex items-center justify-center text-white w-full gap-2 px-3 py-2 rounded-lg font-medium transition-all duration-200
                ${
                  isSearching
                    ? "bg-gray-400 cursor-not-allowed opacity-70"
                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:scale-95"
                }
                `}
              >
                {isSearching ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <Search size={20} />
                    <span>Search</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SearchForm;
